<template>
  <!-- Wrapping div for ease of applying styles -->
  <div>
    <component
      :is="inline ? 'form' : 'div'"
      ref="form"
      class="multi-store-options"
      :class="[
        `multi-store-options--${namespace}`,
        inline ? 'multi-store-options--inline' : 'multi-store-options--modal'
      ]"
      :action="action"
      :method="inline ? 'post' : null"
    >
      <template v-if="inline">
        <input
          name="_method"
          type="hidden"
          value="put"
        >

        <input
          name="return_to"
          type="hidden"
          :value="returnTo"
        >

        <p
          class="multi-store-options__inline-title"
          :class="textStyles.title"
          v-text="$string('general.multi_store.options.change_country')"
        />
      </template>

      <!-- Countries -->
      <div class="multi-store-options__country-container form-group">
        <label
          class="text-utility-label-s"
          :class="{ 'visually-hidden': inline }"
          :for="`${namespace}-country-selector`"
          v-text="$string('general.multi_store.options.country')"
        />

        <select
          :id="`${namespace}-country-selector`"
          ref="country"
          v-model="currentCountryCode"
          class="multi-store-options__country"
          :class="textStyles.select"
          name="country_code"
        >
          <option
            v-for="(country) of countries"
            :key="country.code"
            :value="country.code"
            v-text="getCountrySelectName(country)"
          />
        </select>
      </div>

      <!-- Languages -->
      <div
        v-if="!inline"
        class="multi-store-options__language-container form-group"
      >
        <label
          class="text-utility-label-s"
          :for="`${namespace}-language-selector`"
          v-text="$string('general.multi_store.options.language')"
        />

        <select
          :id="`${namespace}-language-selector`"
          ref="language"
          v-model="currentLanguageCode"
          class="multi-store-options__language"
          :class="textStyles.select"
          name="language_code"
        >
          <option
            v-for="(language) of languages"
            :key="language.code"
            :value="language.code"
            v-text="language.name"
          />
        </select>
      </div>

      <input
        v-else
        name="language_code"
        type="hidden"
        :value="languages[0]?.code"
      >

      <!-- Hidden elements used to calculate select widths -->
      <span
        ref="countryText"
        class="multi-store-options__width-element"
        :class="textStyles.select"
        aria-hidden="true"
      />

      <span
        ref="languageText"
        class="multi-store-options__width-element"
        :class="textStyles.select"
        aria-hidden="true"
      />
    </component>
  </div>
</template>

<script>

/**
 * Vue: Multi-store options (multi-store-options)
 * -----------------------------------------------------------------------------
 * Localization form selects with options.
 * - Select names have to those names as they're set by Shopify.
 *
 * @param {String} namespace - Namespace of component.
 *
 */
import { mapActions, mapGetters, mapState } from 'vuex'

import { multiStoreAction, multiStoreReturnTo } from '~/helpers/general'

export default {
  name: 'MultiStoreOptions',

  props: {
    namespace: {
      type: String,
      default: '',
      required: true,
    },
  },

  data() {
    return {
      currentCountryCode: '',
      currentLanguageCode: '',
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getActiveOverlay: 'overlays/getActiveOverlay',
    }),

    /**
     * Map Vuex state.
     */
    ...mapState({
      current: (state) => state['multi-store'].current,
      isMobile: (state) => state.index.screen.mobile,
      screenWidth: (state) => state.index.screen.width,
      selectedStore: (state) => state['multi-store'].selectedStore,
      suggestedStore: (state) => state['multi-store'].suggestedStore,
      supported: (state) => state['multi-store'].supported,
    }),

    /**
     * Compute form action based on current country code.
     * @returns {String}
     */
    action() {
      if (!this.inline) {
        return null
      }

      return multiStoreAction(this.active?.instance)
    },

    /**
     * Compute active value.
     * - This is selected if user has changed select value, or suggested if user
     *   hasn't updated anything yet.
     */
    active() {
      return this.selectedStore || this.suggestedStore
    },

    /**
     * Compute countries array.
     * @returns {Array}
     */
    countries() {
      if (!Object.keys(this.supported).length) {
        return [this.current.country]
      }

      return Object.values(this.supported)
        .map((value) => value.country)
        .sort((a, b) => {
          if (a.name < b.name) {
            return -1
          }

          if (a.name > b.name) {
            return 1
          }

          return 0
        })
    },

    /**
     * Compute if its an inline template.
     * @returns {Boolean}
     */
    inline() {
      return this.namespace !== 'modal'
    },

    /**
     * Compute language choices based on current country.
     * @returns {Array}
     */
    languages() {
      return this.supported[this.currentCountryCode]?.languages?.length
        ? this.supported[this.currentCountryCode].languages
        : [this.current.language]
    },

    /**
     * Compute return to URL.
     * @returns {String}
     */
    returnTo() {
      if (!this.inline) {
        return null
      }

      return multiStoreReturnTo(this.active)
    },

    /**
     * Compute text styles based on namespace/template.
     * @returns {Object}
     */
    textStyles() {
      const textStyles = {
        select: 'text-body-m',
        title: 'text-utility-capitalized-m text-utility-capitalized-s-desktop',
      }

      if (this.inline) {
        textStyles.select = 'text-utility-capitalized-m text-utility-capitalized-s-desktop'
      }

      switch (this.namespace) {
        case 'pre-header-banner':
          textStyles.select = 'text-heading-s'
          textStyles.title = 'text-heading-s'
          break
      }

      return textStyles
    },
  },

  watch: {

    /**
     * Watch current country code to update select width.
     * - Also tests to see if previously selected language exists in country.
     * @param {String} countryCode - New country code.
     */
    currentCountryCode(countryCode) {
      if (!countryCode) {
        return
      }

      const matchedLanguage = this.languages.find((language) => {
        return language?.code === this.currentLanguageCode
      })

      if (!matchedLanguage) {
        this.currentLanguageCode = this.languages[0]?.code
      }

      this.updateSelectWidth('country')

      if (this.supported[countryCode]) {
        this.setSelectedStore({
          country: this.supported[countryCode].country,
          instance: this.supported[countryCode].instance,
          language: matchedLanguage || this.languages[0],
        })
      }

      if (!this.inline) {
        return
      }

      if (countryCode === this.current.country.code) {
        return
      }

      this.submitForm()
    },

    /**
     * Watch current language code to update select width.
     * @param {String} languageCode - New language code.
     */
    currentLanguageCode(languageCode) {
      if (this.inline) {
        return
      }

      const matchedLanguage = this.languages.find((language) => {
        return language.code === languageCode
      })

      this.updateSelectWidth('language')

      if (!this.supported[this.currentCountryCode]) {
        return
      }

      this.setSelectedStore({
        country: this.supported[this.currentCountryCode].country,
        instance: this.supported[this.currentCountryCode].instance,
        language: matchedLanguage || this.languages[0],
      })
    },

    /**
     * Watch active overlay to update menu drawer select width.
     */
    getActiveOverlay() {
      if (
        this.namespace !== 'menu-drawer' ||
        this.getActiveOverlay !== 'menuDrawer'
      ) {
        return
      }

      this.updateSelectWidth('country')
    },

    /**
     * Watch screen width to update inline select widths.
     */
    screenWidth() {
      if (!this.inline) {
        return
      }

      this.updateSelectWidth('country')
    },

    /**
     * Watch suggested store to udpate default selection.
     */
    suggestedStore() {
      this.setCurrentValues()
    },
  },

  mounted() {
    this.setCurrentValues()
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      setPreference: 'multi-store/setPreference',
      setSelectedStore: 'multi-store/setSelectedStore',
    }),

    /**
     * Set current values.
     */
    setCurrentValues() {
      if (this.currentCountryCode || this.currentLanguageCode) {
        return
      }

      /**
       * Inline options should show current store.
       */
      this.currentCountryCode = this.inline
        ? this.current.country?.code || cnvs.store.country
        : this.suggestedStore.country?.code

      this.currentLanguageCode = this.inline
        ? this.current.language?.code || cnvs.store.language
        : this.suggestedStore.language?.code
    },

    /**
     * Get country select option name.
     * @param {Object} country - Current country.
     * @returns {String}
     */
    getCountrySelectName(country) {
      return this.$string('general.multi_store.format', {
        replace: {
          country_code: country.code,
          country_name: country.name,
          currency_code: country.currency,
          currency_symbol: country.currencySymbol,
        },
      })
    },

    /**
     * Update select width variable to match current option.
     * @param {String} select - 'country' or 'language' select.
     */
    async updateSelectWidth(select) {
      await this.$nextTick()

      /**
       * Exemptions.
       * - Pre-header banner options is not rendered on mobile.
       * Don't check width if menu drawer is close
       */
      if (
        (this.namespace === 'pre-header-banner' && this.isMobile) ||
        (this.namespace === 'menu-drawer' && this.getActiveOverlay !== 'menuDrawer')
      ) {
        return
      }

      /**
       * Use text value of option in text element.
       * - Use this element to calculate select's width.
       * - Otherwise select is always the width of the widest option.
       */
      const element = this.$refs[select]
      const option = element.selectedOptions[0]

      if (!option) {
        return
      }

      const textRef = `${select}Text`
      const textElement = this.$refs[textRef]
      textElement.innerText = option?.innerText
      const width = textElement.offsetWidth + 3

      document.documentElement.style.setProperty(
        `--multi-store-option-width-${this.namespace}`,
        `${width}px`,
      )
    },

    /**
     * Submit form on inline select update.
     * - Only triggered on country change as language selector is not shown.
     */
    async submitForm() {
      const form = this.$refs.form
      const formData = new FormData(form)

      const preferences = {
        country: formData.get('country_code'),
        language: formData.get('language_code'),
      }

      if (cnvs.storybook) {
        window.console.log('Submit form', preferences, this.action, this.returnTo)
        return
      }

      /**
       * Set preferences and dismissed so if you come back the modal shows.
       */
      this.setPreference(preferences)
      sessionStorage.setItem('multi-store-dismissed', JSON.stringify(preferences))

      /**
       * Wait for preferences to be set and action/returnTo computed props to
       * finish calculating.
       */
      await this.$nextTick()
      form.submit()
    },
  },
}
</script>
